/**
 * UpgradePromptModal Component
 *
 * Modal that prompts users to upgrade their plan (no credit purchases: BeHeard sells none).
 * Displays contextual messaging based on why the user hit a limit.
 *
 * Supports multiple error types:
 * - USAGE_BLOCKED: a monthly usage allowance (local safety limit) is used up
 * - PLAN_LIMIT: User hit their plan's limit
 * - ORG_PLAN_LIMIT: Organization hit their plan's limit
 */

import { useNavigate } from 'react-router-dom';
/** Usage action type for upgrade prompt context */
type UsageAction =
  | 'voice_practice_classic'
  | 'voice_practice_live_minute'
  | 'tts_coach_response'
  | 'listen_full_pitch'
  | 'listen_snippet'
  | 'exam_attempt'
  | 'portfolio_profile_view';
import { getLimitLabel } from '../../utils/errorHelpers';
import { allowanceLines } from '../../config/planLimitCopy';
import { useCentralBillingLinks } from '../../hooks/useCentralBillingLinks';
import { openCentralBilling } from '../../services/centralBillingUrls';
import { ASK_OWNER_BILLING_COPY } from '../../adapters/settingsAdapter';
import { LIMIT_COPY, PLAN_POLICIES, limitReachedMessage, planDisplayName, type PlanLimitKey } from '@beheard/limits';

/**
 * The refusal wording when the server's own message is missing: the same
 * limit copy the server uses, with the plan's other limits for context.
 */
function fallbackLimitMessage(
  errorCode: UpgradeErrorCode,
  limitKey: string,
  planKey: string | undefined,
  currentValue: number | undefined,
  maxValue: number | undefined
): string {
  if (limitKey in LIMIT_COPY && maxValue !== undefined) {
    return limitReachedMessage({
      key: limitKey as PlanLimitKey,
      max: maxValue,
      current: currentValue ?? maxValue,
      planName: errorCode === 'PLAN_LIMIT' ? planDisplayName(planKey) : 'current',
      related: errorCode === 'PLAN_LIMIT' ? (PLAN_POLICIES[planKey ?? 'free'] as Partial<Record<PlanLimitKey, number>>) : undefined,
    });
  }
  return errorCode === 'PLAN_LIMIT'
    ? `You've reached your limit of ${maxValue} ${getLimitLabel(limitKey)} on the ${planDisplayName(planKey)} plan. Upgrade to continue.`
    : `Your organization has reached its limit of ${maxValue} ${getLimitLabel(limitKey)}. Upgrade your organization's plan to continue.`;
}

export type UpgradeErrorCode = 'USAGE_BLOCKED' | 'PLAN_LIMIT' | 'ORG_PLAN_LIMIT';

export interface UpgradePromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  reason?: string;
  message?: string;
  action?: UsageAction | string;
  // New props for plan limit errors
  errorCode?: UpgradeErrorCode;
  limitKey?: string;
  planKey?: string;
  currentValue?: number;
  maxValue?: number;
}

/**
 * Get CTA button configuration based on reason or error code
 */
function getCtaConfig(reason?: string, errorCode?: UpgradeErrorCode): { primary: string; secondary?: string; primaryPath: string } {
  // Handle new error codes first
  if (errorCode === 'PLAN_LIMIT') {
    return {
      primary: 'Manage Plan',
      secondary: 'Maybe Later',
      primaryPath: '/settings/billing',
    };
  }
  if (errorCode === 'ORG_PLAN_LIMIT') {
    return {
      primary: 'Manage Plan',
      secondary: 'Maybe Later',
      primaryPath: '/settings/billing',
    };
  }

  switch (reason) {
    case 'plan_not_allowed':
    case 'ai':
    case 'live_voice':
    case 'tts':
      return {
        primary: 'Manage Plan',
        secondary: 'Maybe Later',
        primaryPath: '/settings/billing',
      };
    case 'credits_exhausted':
    case 'listen':
      return {
        primary: 'Manage Plan',
        secondary: 'Maybe Later',
        primaryPath: '/settings/billing',
      };
    case 'daily_limit_reached':
    case 'monthly_limit_reached':
    case 'plan_limit':
      return {
        primary: 'Manage Plan',
        secondary: 'Maybe Later',
        primaryPath: '/settings/billing',
      };
    default:
      return {
        primary: 'Manage Plan',
        primaryPath: '/settings/billing',
      };
  }
}

/**
 * Get icon for the modal based on reason or error code
 */
function getModalIcon(reason?: string, errorCode?: UpgradeErrorCode): string {
  // Handle error codes first
  if (errorCode === 'PLAN_LIMIT') return '📊';
  if (errorCode === 'ORG_PLAN_LIMIT') return '🏢';

  switch (reason) {
    case 'credits_exhausted':
    case 'listen':
      return '🎧';
    case 'ai':
    case 'tts':
      return '🤖';
    case 'live_voice':
      return '🎙️';
    case 'daily_limit_reached':
    case 'monthly_limit_reached':
    case 'plan_limit':
      return '📊';
    default:
      return '⬆️';
  }
}

/**
 * Get title for the modal based on error code
 */
function getModalTitle(errorCode?: UpgradeErrorCode, reason?: string): string {
  if (errorCode === 'PLAN_LIMIT') return 'Plan Limit Reached';
  if (errorCode === 'ORG_PLAN_LIMIT') return 'Organization Limit Reached';
  if (reason === 'credits_exhausted' || reason === 'listen') return 'Monthly Allowance Used';
  return 'Upgrade Your Plan';
}

export function UpgradePromptModal({
  isOpen,
  onClose,
  reason,
  message,
  action: _action,
  errorCode,
  limitKey,
  planKey,
  currentValue,
  maxValue,
}: UpgradePromptModalProps) {
  const navigate = useNavigate();
  const { canManage, urlFor } = useCentralBillingLinks();
  const baseCta = getCtaConfig(reason, errorCode);
  // Suite contract 1.2: owners/admins (manageBilling) go to Central's plans page;
  // a member cannot change the plan, so they are told who can — never handed a
  // "Manage Plan" button into a billing page they cannot use.
  const ctaConfig = canManage
    ? { ...baseCta, primary: 'Upgrade in LevelUp' }
    : { primary: 'Got it', secondary: undefined, primaryPath: baseCta.primaryPath };
  const icon = getModalIcon(reason, errorCode);
  const title = getModalTitle(errorCode, reason);

  const handlePrimaryAction = () => {
    onClose();
    if (!canManage) return;
    const plansUrl = urlFor('plans');
    if (plansUrl) {
      openCentralBilling(plansUrl);
      return;
    }
    // Central not addressable (no workspace yet): the in-app billing tab says why.
    navigate(ctaConfig.primaryPath);
  };

  const handleSecondaryAction = () => {
    onClose();
  };

  // Build message for plan limit errors
  const displayMessage = message || (
    (errorCode === 'PLAN_LIMIT' || errorCode === 'ORG_PLAN_LIMIT') && limitKey
      ? fallbackLimitMessage(errorCode, limitKey, planKey, currentValue, maxValue)
      : 'Upgrade to access this feature and unlock your full potential.'
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header with gradient */}
        <div className="bg-gradient-to-br from-primary-500 to-primary-700 px-6 py-8 text-center">
          <div className="text-5xl mb-4">{icon}</div>
          <h2 className="text-xl font-bold text-white">{title}</h2>
        </div>

        {/* Content */}
        <div className="px-6 py-6 space-y-4">
          <p className="text-gray-600 text-center">
            {displayMessage}
          </p>

          {/* Plan limit usage indicator */}
          {(errorCode === 'PLAN_LIMIT' || errorCode === 'ORG_PLAN_LIMIT') && currentValue !== undefined && maxValue !== undefined && (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-600">Current Usage</span>
                <span className="text-sm font-medium text-gray-900">
                  {currentValue} / {maxValue}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-red-500 h-2 rounded-full"
                  style={{ width: '100%' }}
                />
              </div>
            </div>
          )}

          {/* Feature highlight for plan upgrades */}
          {(reason === 'plan_not_allowed' || reason === 'ai' || reason === 'live_voice') && (
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <p className="text-sm font-medium text-gray-700">
                Pro + AI plan includes:
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                {/* R0.1: the two bullets below previously advertised the realtime
                    conversation experience. That path is intentionally unreachable in
                    deployed builds, so selling it as an included feature — at the
                    moment we ask for payment — was the most user-facing untruth left.
                    Replaced with capabilities that work today. */}
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span>
                  AI coaching conversations with practice personas
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span>
                  Record &amp; review voice practice with AI feedback
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span>
                  Voice feedback from your coach
                </li>
                {/* Allowances as the Pro + AI plan's capabilities define and enforce them. */}
                {allowanceLines('pro_ai').map((line) => (
                  <li key={line} className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    {line}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!canManage && (
            <p className="text-sm text-gray-600 text-center" data-testid="upgrade-ask-owner">
              {ASK_OWNER_BILLING_COPY}
            </p>
          )}

          {/* Allowance used up — it resets monthly; nothing can be bought */}
          {(reason === 'credits_exhausted' || reason === 'listen') && (
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <p className="text-sm text-gray-600">
                This is a monthly usage allowance, not a credit balance: it resets at
                the start of next month. A different plan in LevelUp may include more.
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-6 pb-6 space-y-3">
          <button
            onClick={handlePrimaryAction}
            className="w-full px-4 py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors"
          >
            {ctaConfig.primary}
          </button>
          {ctaConfig.secondary && (
            <button
              onClick={handleSecondaryAction}
              className="w-full px-4 py-3 text-gray-600 bg-gray-100 font-medium rounded-lg hover:bg-gray-200 transition-colors"
            >
              {ctaConfig.secondary}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Inline upgrade banner for embedding in pages
 */
export function UpgradeBanner({
  message,
  actionLabel = 'Upgrade',
  onAction,
}: {
  message: string;
  actionLabel?: string;
  onAction: () => void;
}) {
  return (
    <div className="bg-gradient-to-r from-primary-50 to-primary-100 border border-primary-200 rounded-lg p-4 flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <span className="text-2xl">✨</span>
        <p className="text-sm text-primary-800">{message}</p>
      </div>
      <button
        onClick={onAction}
        className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors whitespace-nowrap"
      >
        {actionLabel}
      </button>
    </div>
  );
}
